/**
 * Admin Panel Servis Katmanı
 *
 * Tüm admin paneli veritabanı işlemlerini merkezi olarak yönetir.
 * Supabase client üzerinden RLS korumalı sorgular çalıştırır.
 * Sadece admin rolüne sahip kullanıcılar bu servisi kullanabilir.
 *
 * @module adminService
 */

import { supabase } from '../lib/supabase';

const adminService = {
  // ===========================================================
  // Dashboard İstatistikleri
  // ===========================================================

  /**
   * Admin dashboard için genel istatistikleri döndürür.
   * get_admin_dashboard_stats() RPC fonksiyonunu çağırır.
   * @returns {{ data: object|null, error: object|null }}
   */
  async getDashboardStats() {
    try {
      const { data, error } = await supabase.rpc('get_admin_dashboard_stats');
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[AdminService] getDashboardStats error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Kullanıcı Yönetimi
  // ===========================================================

  /**
   * Tüm kullanıcıları listeler (filtreleme + sayfalama destekli).
   * @param {{ role?: string, search?: string, isActive?: boolean, limit?: number, offset?: number }} options
   * @returns {{ data: array|null, count: number, error: object|null }}
   */
  async getUsers({ role = null, search = null, isActive = null, limit = 50, offset = 0 } = {}) {
    try {
      let query = supabase
        .from('user_profiles')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (role) {
        query = query.or(`role.eq.${role},roles.cs.{${role}}`);
      }

      if (isActive !== null) {
        query = query.eq('is_active', isActive);
      }

      if (search) {
        query = query.or(`full_name.ilike.%${search}%,phone.ilike.%${search}%`);
      }

      const { data, error, count } = await query;
      return { data, count, error };
    } catch (error) {
      console.error('[AdminService] getUsers error:', error);
      return { data: null, count: 0, error };
    }
  },

  /**
   * Tek bir kullanıcının detaylı bilgilerini döndürür.
   * @param {string} userId
   * @returns {{ data: object|null, error: object|null }}
   */
  async getUserDetail(userId) {
    try {
      const { data, error } = await supabase.rpc('get_admin_user_detail', {
        target_user_id: userId,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[AdminService] getUserDetail error:', error);
      return { data: null, error };
    }
  },

  /**
   * Kullanıcının auth email bilgisini getirir (admin_users_view'dan).
   * @param {string} userId
   * @returns {{ data: object|null, error: object|null }}
   */
  async getUserEmail(userId) {
    try {
      // admin_users_view view'ı auth.users ile join yapıyor
      const { data, error } = await supabase
        .from('admin_users_view')
        .select('email, last_sign_in_at, email_confirmed_at, user_created_at')
        .eq('id', userId)
        .single();
      return { data, error };
    } catch (error) {
      console.error('[AdminService] getUserEmail error:', error);
      return { data: null, error };
    }
  },

  /**
   * Kullanıcı durumunu günceller (aktif/pasif).
   * @param {string} userId
   * @param {boolean} isActive
   * @returns {{ data: boolean, error: object|null }}
   */
  async updateUserStatus(userId, isActive) {
    try {
      const { data, error } = await supabase.rpc('admin_update_user_status', {
        target_user_id: userId,
        new_status: isActive,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[AdminService] updateUserStatus error:', error);
      return { data: null, error };
    }
  },

  /**
   * Kullanıcıya rol ekler.
   * @param {string} userId
   * @param {string} role
   * @returns {{ data: boolean, error: object|null }}
   */
  async addUserRole(userId, role) {
    try {
      const { data, error } = await supabase.rpc('add_role_to_user', {
        user_id: userId,
        new_role: role,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[AdminService] addUserRole error:', error);
      return { data: null, error };
    }
  },

  /**
   * Kullanıcıdan rol kaldırır.
   * @param {string} userId
   * @param {string} role
   * @returns {{ data: boolean, error: object|null }}
   */
  async removeUserRole(userId, role) {
    try {
      const { data, error } = await supabase.rpc('remove_role_from_user', {
        user_id: userId,
        role_to_remove: role,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[AdminService] removeUserRole error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Yatırım Yönetimi
  // ===========================================================

  /**
   * Tüm yatırımları listeler (filtreleme + sayfalama destekli).
   * @param {{ status?: string, userId?: string, limit?: number, offset?: number }} options
   * @returns {{ data: array|null, count: number, error: object|null }}
   */
  async getInvestments({ status = null, userId = null, limit = 50, offset = 0 } = {}) {
    try {
      let query = supabase
        .from('investor_investments')
        .select('*, property:properties(id, title_tr, title_en, price_gbp, type, location_tr)', { count: 'exact' })
        .order('updated_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (status) {
        query = query.eq('status', status);
      }

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error, count } = await query;

      // Her yatırım için kullanıcı bilgisini çek
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map((inv) => inv.user_id))];
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, full_name, phone')
          .in('id', userIds);

        const profileMap = {};
        profiles?.forEach((p) => {
          profileMap[p.id] = p;
        });

        data.forEach((inv) => {
          inv.user_profile = profileMap[inv.user_id] || null;
        });
      }

      return { data, count, error };
    } catch (error) {
      console.error('[AdminService] getInvestments error:', error);
      return { data: null, count: 0, error };
    }
  },

  /**
   * Yatırım durumunu günceller.
   * @param {string} investmentId
   * @param {string} status
   * @param {string|null} notes
   * @returns {{ data: boolean, error: object|null }}
   */
  async updateInvestmentStatus(investmentId, status, notes = null) {
    try {
      const { data, error } = await supabase.rpc('admin_update_investment_status', {
        investment_id: investmentId,
        new_status: status,
        admin_notes: notes,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[AdminService] updateInvestmentStatus error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Belge Yönetimi
  // ===========================================================

  /**
   * Tüm belgeleri listeler (filtreleme + sayfalama destekli).
   * @param {{ status?: string, userId?: string, documentType?: string, limit?: number, offset?: number }} options
   * @returns {{ data: array|null, count: number, error: object|null }}
   */
  async getDocuments({ status = null, userId = null, documentType = null, limit = 50, offset = 0 } = {}) {
    try {
      let query = supabase
        .from('investor_documents')
        .select('*', { count: 'exact' })
        .order('uploaded_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (status) {
        query = query.eq('status', status);
      }

      if (userId) {
        query = query.eq('user_id', userId);
      }

      if (documentType) {
        query = query.eq('document_type', documentType);
      }

      const { data, error, count } = await query;

      // Her belge için kullanıcı bilgisini çek
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map((doc) => doc.user_id))];
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, full_name, phone')
          .in('id', userIds);

        const profileMap = {};
        profiles?.forEach((p) => {
          profileMap[p.id] = p;
        });

        data.forEach((doc) => {
          doc.user_profile = profileMap[doc.user_id] || null;
        });
      }

      return { data, count, error };
    } catch (error) {
      console.error('[AdminService] getDocuments error:', error);
      return { data: null, count: 0, error };
    }
  },

  /**
   * Belge durumunu günceller (doğrula/reddet).
   * @param {string} documentId
   * @param {string} status - 'verified' | 'rejected' | 'pending'
   * @param {string|null} notes
   * @returns {{ data: boolean, error: object|null }}
   */
  async verifyDocument(documentId, status, notes = null) {
    try {
      const { data, error } = await supabase.rpc('admin_verify_document', {
        doc_id: documentId,
        new_status: status,
        admin_notes: notes,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[AdminService] verifyDocument error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // İşlem Yönetimi (Transactions)
  // ===========================================================

  /**
   * Tüm finansal işlemleri listeler.
   * @param {{ status?: string, userId?: string, limit?: number, offset?: number }} options
   * @returns {{ data: array|null, count: number, error: object|null }}
   */
  async getTransactions({ status = null, userId = null, limit = 50, offset = 0 } = {}) {
    try {
      let query = supabase
        .from('investor_transactions')
        .select('*', { count: 'exact' })
        .order('transaction_date', { ascending: false })
        .range(offset, offset + limit - 1);

      if (status) {
        query = query.eq('status', status);
      }

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error, count } = await query;

      // Her işlem için kullanıcı bilgisini çek
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map((tx) => tx.user_id))];
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, full_name, phone')
          .in('id', userIds);

        const profileMap = {};
        profiles?.forEach((p) => {
          profileMap[p.id] = p;
        });

        data.forEach((tx) => {
          tx.user_profile = profileMap[tx.user_id] || null;
        });
      }

      return { data, count, error };
    } catch (error) {
      console.error('[AdminService] getTransactions error:', error);
      return { data: null, count: 0, error };
    }
  },

  // ===========================================================
  // Kullanıcı Aktiviteleri
  // ===========================================================

  /**
   * Kullanıcı aktivite loglarını listeler.
   * @param {{ userId?: string, action?: string, limit?: number, offset?: number }} options
   * @returns {{ data: array|null, count: number, error: object|null }}
   */
  async getActivityLogs({ userId = null, action = null, limit = 50, offset = 0 } = {}) {
    try {
      let query = supabase
        .from('user_activity_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (userId) {
        query = query.eq('user_id', userId);
      }

      if (action) {
        query = query.eq('action', action);
      }

      const { data, error, count } = await query;
      return { data, count, error };
    } catch (error) {
      console.error('[AdminService] getActivityLogs error:', error);
      return { data: null, count: 0, error };
    }
  },

  // ===========================================================
  // Kullanıcıya Ait Detay Verileri
  // ===========================================================

  /**
   * Belirli bir kullanıcının yatırımlarını getirir.
   * @param {string} userId
   * @returns {{ data: array|null, error: object|null }}
   */
  async getUserInvestments(userId) {
    try {
      const { data, error } = await supabase
        .from('investor_investments')
        .select('*, property:properties(id, title_tr, title_en, price_gbp, type, location_tr)')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('[AdminService] getUserInvestments error:', error);
      return { data: null, error };
    }
  },

  /**
   * Belirli bir kullanıcının belgelerini getirir.
   * @param {string} userId
   * @returns {{ data: array|null, error: object|null }}
   */
  async getUserDocuments(userId) {
    try {
      const { data, error } = await supabase
        .from('investor_documents')
        .select('*')
        .eq('user_id', userId)
        .order('uploaded_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('[AdminService] getUserDocuments error:', error);
      return { data: null, error };
    }
  },

  /**
   * Belirli bir kullanıcının işlemlerini getirir.
   * @param {string} userId
   * @returns {{ data: array|null, error: object|null }}
   */
  async getUserTransactions(userId) {
    try {
      const { data, error } = await supabase
        .from('investor_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('transaction_date', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('[AdminService] getUserTransactions error:', error);
      return { data: null, error };
    }
  },

  /**
   * Belirli bir kullanıcının favorilerini getirir.
   * @param {string} userId
   * @returns {{ data: array|null, error: object|null }}
   */
  async getUserFavorites(userId) {
    try {
      const { data, error } = await supabase
        .from('investor_favorites')
        .select('*, property:properties(id, title_tr, title_en, price_gbp, type, location_tr)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('[AdminService] getUserFavorites error:', error);
      return { data: null, error };
    }
  },

  /**
   * Belirli bir kullanıcının ilan görüntüleme geçmişini getirir.
   * @param {string} userId
   * @returns {{ data: array|null, error: object|null }}
   */
  async getUserPropertyViews(userId) {
    try {
      const { data, error } = await supabase
        .from('investor_property_views')
        .select('*, property:properties(id, title_tr, title_en, price_gbp, type, location_tr)')
        .eq('user_id', userId)
        .order('viewed_at', { ascending: false })
        .limit(50);

      return { data, error };
    } catch (error) {
      console.error('[AdminService] getUserPropertyViews error:', error);
      return { data: null, error };
    }
  },
};

export default adminService;
